import axios, { AxiosError, CancelToken } from 'axios';
import { getInvoiceSummariesUrl } from 'utilities/invoiceServiceV2';
import {
  OperationSummaryParams,
  OperationSummaryResponse,
  SortConfig,
  SORT_BY_VALUES,
  SORT_ORDER_VALUES
} from 'components/RecordsListView/types';
import {
  getRequestConfig,
  handleOperationSummaryError
} from 'screens/Applications&WorkOrders/apiHelpers';
import { InvoiceSortConfig, InvoiceSummary } from '../types';

const DEFAULT_PAGE = 0;
const DEFAULT_SIZE = 50;

/** Matches the redesigned Invoices landing's Invoice Date (New→Old) default. */
const DEFAULT_INVOICE_SORT: InvoiceSortConfig = { by: 'date', dir: 'desc' };

const getSelectedOrganizationId = (): string =>
  localStorage.getItem('selectedOrganizationId') ?? '';

/**
 * Translates the generic operation-summary `SortConfig` into the invoice
 * endpoint's `sortBy` (`name` | `date`) / `sortDir` (`asc` | `desc`) params.
 */
const mapSortToInvoiceSort = (sorts?: SortConfig[]): InvoiceSortConfig => {
  const primary = sorts?.[0];
  if (!primary) return DEFAULT_INVOICE_SORT;
  return {
    by: primary.sortBy === SORT_BY_VALUES.NAME ? 'name' : 'date',
    dir: primary.order === SORT_ORDER_VALUES.DESC ? 'desc' : 'asc'
  };
};

/**
 * Fetch function backing `useInvoicesSummary`. Calls
 * `GET /v1/invoice/summaries` (the same endpoint exposed by the `invoice` axios
 * resource and the legacy `useInvoices` hook) and normalizes the response into
 * `OperationSummaryResponse<InvoiceSummary>` so it can be consumed directly by
 * the shared `useOperationSummary` hook.
 *
 * The request shape mirrors the legacy `useInvoices` call (invoice-specific
 * param names `pageNo`, `sortBy`, `sortDir`, `searchBy`, `startDate`/`endDate`,
 * and a csv `cropSeasonIds`). On any request error a normalized empty response
 * carrying `errors` is returned (parity with the Applications summary fetch);
 * error toasts are surfaced by the consuming hook / global interceptors.
 */
const getSummaryInvoices = async (
  params: OperationSummaryParams = {},
  cancelToken?: CancelToken
): Promise<OperationSummaryResponse<InvoiceSummary>> => {
  const { page, size, filters, name, fromDate, toDate, cropSeasonIds } = params;
  const searchBy = filters?.name ?? name ?? '';
  const { by: sortBy, dir: sortDir } = mapSortToInvoiceSort(params.sorts);

  try {
    const response = await axios.get<OperationSummaryResponse<InvoiceSummary>>(
      getInvoiceSummariesUrl(),
      {
        ...getRequestConfig(getSelectedOrganizationId(), cancelToken),
        params: {
          size: size ?? DEFAULT_SIZE,
          pageNo: page ?? DEFAULT_PAGE,
          sortBy,
          sortDir,
          cropSeasonIds: (cropSeasonIds ?? []).join(','),
          ...(searchBy ? { searchBy } : {}),
          ...(fromDate && toDate
            ? { startDate: fromDate, endDate: toDate }
            : {})
        }
      }
    );

    const { data } = response;
    return {
      results: data?.results ?? [],
      totalElements: data?.totalElements ?? 0,
      totalPages: data?.totalPages ?? 0,
      size: data?.size ?? DEFAULT_SIZE,
      currentPage: data?.currentPage ?? DEFAULT_PAGE,
      numberOfElements: data?.numberOfElements ?? 0,
      errors: data?.errors ?? null
    };
  } catch (error) {
    return handleOperationSummaryError<InvoiceSummary>(error as AxiosError)
      .data;
  }
};

export default getSummaryInvoices;
